import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, SearchIcon } from 'lucide-react'
import {
	Button,
	Dialog, DialogContent, DialogHeader,
	Input,
	Spinner,
} from '@rentflowdev/ui'
import { useListPropertiesQuery, useListRentalsQuery } from '@/api'
import { formatPropertyAddress } from '@/lib/property-address'
import type { Property, Rental } from '@/types'

type Step = 'property' | 'rental'

interface DuplicateAutomationDialogProps {
	open:           boolean
	onOpenChange:   (open: boolean) => void
	onRentalPicked: (rentalId: string, rental: Rental) => void
}

function formatDate(iso?: string): string {
	if (!iso) return '—'
	return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function PropertyRow({ property, onClick }: { property: Property; onClick: () => void }) {
	const { t } = useTranslation()
	const label = formatPropertyAddress(property.city, property.address)
	const sub = [
		t(`properties.types.${property.type}`),
		property.rooms ? `${property.rooms} комн.` : undefined,
		property.area ? `${property.area} м²` : undefined,
	].filter(Boolean).join(' · ')

	return (
		<button
			type="button"
			onClick={onClick}
			className="flex w-full items-center gap-3 rounded-(--radius-md) px-3 py-2.5 text-left hover:bg-(--color-muted)/60 transition-colors"
		>
			<div className="size-10 shrink-0 overflow-hidden rounded-(--radius-md) bg-(--color-muted)">
				{property.images?.[0]?.url && (
					<img src={property.images[0].url} alt="" className="size-full object-cover" />
				)}
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<span className="truncate text-sm font-[500] text-(--color-foreground)">{label}</span>
				<span className="text-xs text-(--color-muted-foreground)">{sub || t('common.noData', { defaultValue: '—' })}</span>
			</div>
		</button>
	)
}

function RentalRow({ rental, onClick }: { rental: Rental; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex w-full items-center gap-3 rounded-(--radius-md) px-3 py-2.5 text-left hover:bg-(--color-muted)/60 transition-colors"
		>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<span className="truncate text-sm font-[500] text-(--color-foreground)">
					{rental.tenantName ?? '—'}
				</span>
				<span className="text-xs text-(--color-muted-foreground)">
					{formatDate(rental.startDate)} — {formatDate(rental.endDate)}
				</span>
			</div>
		</button>
	)
}

export function DuplicateAutomationDialog({ open, onOpenChange, onRentalPicked }: DuplicateAutomationDialogProps) {
	const { t } = useTranslation()
	const [step, setStep] = useState<Step>('property')
	const [query, setQuery] = useState('')
	const [pickedProperty, setPickedProperty] = useState<Property | null>(null)

	const { data: propertiesData, isLoading: propertiesLoading } = useListPropertiesQuery(
		{ limit: 200 },
		{ skip: !open },
	)

	const { data: rentalsData, isLoading: rentalsLoading } = useListRentalsQuery(
		{ propertyId: pickedProperty?.id, limit: 50 },
		{ skip: !pickedProperty },
	)

	const allProperties = propertiesData?.properties ?? []
	const filteredProperties = useMemo(() => {
		const q = query.trim().toLowerCase()
		if (!q) return allProperties
		return allProperties.filter((p) => {
			const hay = `${p.city ?? ''} ${p.address ?? ''}`.toLowerCase()
			return hay.includes(q)
		})
	}, [allProperties, query])

	const activeRentals = useMemo(
		() => (rentalsData?.rentals ?? []).filter((r) => r.status === 'ACTIVE' || r.status === 'UPCOMING'),
		[rentalsData],
	)

	function handlePropertyPick(property: Property) {
		setPickedProperty(property)
		setStep('rental')
	}

	function handleRentalPick(rental: Rental) {
		onRentalPicked(rental.id, rental)
		handleClose()
	}

	function handleClose() {
		onOpenChange(false)
		setStep('property')
		setQuery('')
		setPickedProperty(null)
	}

	function handleBack() {
		setStep('property')
		setPickedProperty(null)
		setQuery('')
	}

	const isPropertyStep = step === 'property'
	const title = isPropertyStep
		? t('rent.automation.duplicate.pickProperty', { defaultValue: 'Выберите объект' })
		: t('rent.automation.duplicate.pickRental', { defaultValue: 'Выберите договор аренды' })
	const subtitle = isPropertyStep
		? t('rent.automation.duplicate.pickPropertySubtitle', { defaultValue: 'Настройки автоматизации будут скопированы на выбранный договор' })
		: pickedProperty?.address ?? undefined

	return (
		<Dialog open={open} onOpenChange={handleClose} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col gap-0 overflow-hidden p-0 rounded-(--radius-lg)"
				style={{ width: 520, maxWidth: '95vw', maxHeight: '80vh' }}
			>
				<DialogHeader border title={title} subtitle={subtitle} />
				{!isPropertyStep && (
					<button
						type="button"
						onClick={handleBack}
						className="absolute left-4 top-4 flex items-center gap-1.5 text-sm text-(--color-muted-foreground) hover:text-(--color-foreground) transition-colors"
					>
						<ArrowLeftIcon className="size-4" />
					</button>
				)}

				{isPropertyStep && (
					<div className="border-b border-(--color-border) px-4 py-3">
						<div className="relative">
							<SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-muted-foreground)" />
							<Input
								autoFocus
								placeholder={t('rent.automation.duplicate.searchPlaceholder', { defaultValue: 'Поиск по адресу…' })}
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								className="pl-9"
							/>
						</div>
					</div>
				)}

				<div className="flex min-h-0 flex-1 flex-col overflow-auto px-2 py-2">
					{isPropertyStep ? (
						propertiesLoading ? (
							<div className="flex items-center justify-center py-12">
								<Spinner />
							</div>
						) : filteredProperties.length === 0 ? (
							<p className="py-12 text-center text-sm text-(--color-muted-foreground)">
								{t('rent.automation.duplicate.noProperties', { defaultValue: 'Объекты не найдены' })}
							</p>
						) : (
							filteredProperties.map((p) => (
								<PropertyRow key={p.id} property={p} onClick={() => handlePropertyPick(p)} />
							))
						)
					) : (
						rentalsLoading ? (
							<div className="flex items-center justify-center py-12">
								<Spinner />
							</div>
						) : activeRentals.length === 0 ? (
							<div className="flex flex-col items-center gap-3 py-12">
								<p className="text-sm text-(--color-muted-foreground)">
									{t('rent.automation.duplicate.noRentals', { defaultValue: 'Нет активных договоров для этого объекта' })}
								</p>
								<Button variant="secondary" size="sm" onClick={handleBack}>
									{t('common.back', { defaultValue: 'Назад' })}
								</Button>
							</div>
						) : (
							activeRentals.map((r) => (
								<RentalRow key={r.id} rental={r} onClick={() => handleRentalPick(r)} />
							))
						)
					)}
				</div>

				<div className="flex items-center justify-end gap-2 border-t border-(--color-border) px-4 py-3">
					<Button variant="secondary" onClick={handleClose}>
						{t('common.cancel')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
